import React, { useEffect, useState } from "react"
import CCardToggle from "../elements/c_card_toggle"
import DfbHeadline from "../ui-kit/dfb_headline"
import DfbParagraph from "../ui-kit/dfb_paragraph"
import DfbToggle from "../ui-kit/dfb_toggle"
import {
  getSubscriptionKeys,
  addSubscriptionKey,
  deleteSubscriptionKey,
  addAllSubscriptionKeys,
  deleteAllSubscriptionKeys,
} from "../../services/categorySubscriptions"
import ServiceError from "../../services/ServiceError"
import {
  activateMailNotifications,
  deactivateMailNotifications,
  isMailNotificationActivated,
} from "../../services/mailNotification"
import content from "../../content"
import { useCategoryItems } from "../../hooks/pageCategories"

const MSubscriptions = ({ headline, title }) => {
  const categoryItems = useCategoryItems()

  const [errorMessage, setErrorMessage] = useState(null)
  const [subscriptionKeys, setSubscriptionKeys] = useState({})
  const [isAllKeysSubscribed, setIsAllKeysSubscribed] = useState(null)
  const [isMailNotificationActive, setIsMailNotificationActive] = useState(null)
  const [cardUpdate, setCardUpdate] = useState(null)

  const init = async () => {
    try {
      const keys = await getSubscriptionKeys()
      updateSubscriptionKeys(
        createAllSubscriptionKeys(key => keys.indexOf(key) !== -1)
      )
      const mailNotification = await isMailNotificationActivated()
      setIsMailNotificationActive(mailNotification)
    } catch (err) {
      handleApiError(err)
    }
  }

  const createAllSubscriptionKeys = computeHandler => {
    const newKeys = categoryItems.reduce((result, item) => {
      const key = item.uid.toString()
      result[key] = computeHandler(key)
      return result
    }, {})
    return newKeys
  }

  const updateSubscriptionKeys = newKeys => {
    setSubscriptionKeys(newKeys)
    const allKeysSubscribed = Object.values(newKeys).every(Boolean)
    setIsAllKeysSubscribed(allKeysSubscribed)
  }

  const handleSubscribeAll = async () => {
    setIsAllKeysSubscribed(null)
    try {
      let newStatus = !isAllKeysSubscribed
      if (isAllKeysSubscribed) {
        deleteAllSubscriptionKeys()
      } else {
        addAllSubscriptionKeys()
      }
      setIsAllKeysSubscribed(newStatus)
      setSubscriptionKeys(createAllSubscriptionKeys(() => newStatus))
    } catch (err) {
      handleApiError(err)
    }
  }

  const handleMailNotification = async () => {
    setIsMailNotificationActive(null)
    try {
      let newStatus = isMailNotificationActive
        ? await deactivateMailNotifications()
        : await activateMailNotifications()
      setIsMailNotificationActive(newStatus)
    } catch (err) {
      handleApiError(err)
    }
  }

  const handleSubscription = async subscriptionKey => {
    try {
      setCardUpdate({ key: [subscriptionKey], value: null })
      if (subscriptionKeys[subscriptionKey] !== true) {
        await addSubscriptionKey(subscriptionKey)
        setCardUpdate({ key: [subscriptionKey], value: true })
      } else {
        await deleteSubscriptionKey(subscriptionKey)
        setCardUpdate({ key: [subscriptionKey], value: false })
      }
    } catch (err) {
      handleApiError(err)
    }
  }

  function handleApiError(err) {
    if (err.statusCode === ServiceError.STATUS_UNAUTHORIZED) {
      setErrorMessage(content.userPanels.subscriptions.error.unauthorized)
    } else {
      setErrorMessage(content.global.error.apiError)
    }
  }

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (cardUpdate != null) {
      const newKeys = {
        ...subscriptionKeys,
        [cardUpdate.key]: cardUpdate.value,
      }
      if (cardUpdate.value != null) {
        updateSubscriptionKeys(newKeys)
      } else {
        setSubscriptionKeys(newKeys)
      }
      setCardUpdate(null)
    }
  }, [cardUpdate])

  return (
    <section className="m-Subscriptions">
      <div className="m-Subscriptions-wrapper">
        <div className="m-Subscriptions-container">
          <DfbHeadline
            headline={headline}
            headline_level="h3"
            add_class="m-Subscriptions-headline"
          />

          {errorMessage ? (
            <DfbParagraph
              text={errorMessage}
              add_class="m-Subscriptions-error-message"
            />
          ) : (
            ""
          )}

          <div className="m-Subscriptions-main">
            <DfbHeadline
              headline={title}
              headline_level="h4"
              add_class="m-Subscriptions-title"
            />
            <DfbParagraph
              text={content.userPanels.subscriptions.description}
              add_class="m-Subscriptions-description"
            />

            <div className="m-Subscriptions-settings">
              <DfbToggle
                labelUnchecked=""
                labelChecked={
                  isAllKeysSubscribed === true
                    ? content.userPanels.subscriptions.unsubscribeAllLabel
                    : content.userPanels.subscriptions.subscribeAllLabel
                }
                disabled={isAllKeysSubscribed == null}
                checked={isAllKeysSubscribed === true}
                add_class="m-Subscriptions-settings-toggle"
                onChange={handleSubscribeAll}
              />
            </div>
            <div className="m-Subscriptions-settings" style={{marginBottom: 0}}>
              <DfbToggle
                labelUnchecked=""
                labelChecked={content.userPanels.subscriptions.notification}
                disabled={isMailNotificationActive == null}
                checked={isMailNotificationActive === true}
                add_class="m-Subscriptions-settings-toggle"
                onChange={handleMailNotification}
                add_style={{border: "0"}}
              />
            </div>
            <DfbParagraph
                text={content.userPanels.subscriptions.disclaimer}
                add_class="m-Subscriptions-description"
            />

            <div className="m-Subscriptions-categories">
              {categoryItems.map(category => {
                const subscriptionKey = category.uid.toString()
                const disabled = subscriptionKeys[subscriptionKey] == null
                const checked = subscriptionKeys[subscriptionKey] === true

                const onClick = event => {
                  event.preventDefault()
                  handleSubscription(subscriptionKey)
                }

                return (
                  <div
                    className={`
                      m-Subscriptions-card
                      ${disabled ? "m-Subscriptions-card--disabled" : ""}
                      ${checked ? "m-Subscriptions-card--checked" : ""}
                    `}
                    key={subscriptionKey}
                  >
                    <CCardToggle
                      {...category}
                      disabled={disabled}
                      checked={checked}
                      onClick={onClick}
                    />
                  </div>
                )
              })}

              {categoryItems.length > 5 &&
                Array.from(Array(4).keys()).map(index => (
                  <div
                    className="m-Subscriptions-card m-Subscriptions-card--placeholder"
                    key={index}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MSubscriptions
